<template>
    <div class="builds-container" :class="{ 'builds-container--builder': usesBuilderLayout }">
        <h4 v-if="getBuildsLoaded" class="builds-heading">Recent builds</h4>
        <div v-if="getBuildsLoaded" class="builds-list">
            <div v-for="(build, index) in builds" :key="index" class="build-item">
                <!-- Header: thumbnail + title/meta + price/clone -->
                <div class="rb-header">
                    <div v-if="build.thumb" class="rb-thumb" :style="{ backgroundImage: 'url(' + build.thumb + ')' }"></div>
                    <div v-else class="rb-thumb rb-thumb--empty"><span class="icon icon-tools"></span></div>

                    <div class="rb-headmain">
                        <div class="rb-title-row">
                            <h4 class="rb-name" :title="build.name">{{ build.name }}</h4>
                            <span class="rb-score" :class="{ 'rb-score--highend': build.overall >= 100 }" :style="{ color: build.overallColor, borderColor: build.overallColor }">
                                <template v-if="build.overall >= 100">High end</template>
                                <template v-else>{{ build.overall }}<span class="rb-score-max">/100</span></template>
                            </span>
                            <span class="icon icon-info-with-circle rb-score-info" v-gixtip="scoreTip(build.overall)"></span>
                        </div>
                        <div class="rb-chips">
                            <span class="rb-chip" v-for="(chip, ci) in build.chips" :key="ci">{{ chip }}</span>
                            <span class="rb-chip rb-chip--watt"><span class="icon icon-flash"></span>~{{ build.tdp }} W</span>
                            <span
                                v-if="build.unavailableCount"
                                class="rb-chip rb-chip--unavailable"
                                title="One or more saved parts are no longer available on Amazon."
                            ><span class="icon icon-warning"></span>{{ build.unavailableCount }} unavailable</span>
                        </div>
                        <div class="rb-actions">
                            <div v-if="showPrices" class="rb-price">
                                <template v-if="build.hasPrice">
                                    <span class="rb-price-cur">$</span>{{ build.priceWhole }}<span class="rb-price-cents">.{{ build.priceCents }}</span>
                                </template>
                                <span v-else class="rb-price-check">Check prices</span>
                            </div>
                            <div v-else class="rb-price rb-price-check">Build summary</div>
                            <!-- Clone starts a NEW build from this one. It must not
                                 adopt the source build's id, or saving would
                                 overwrite the build it was copied from. -->
                            <button @click="cloneBuild(build.raw), scrollToTop()" class="gc-button rb-clone">Clone</button>
                        </div>
                    </div>
                </div>

                <!-- Per-part performance -->
                <div class="rb-specs">
                    <div class="rb-spec" v-for="(spec, si) in build.specs" :key="si">
                        <div class="rb-spec-head">
                            <span class="rb-spec-name"><span class="rb-spec-cat">{{ spec.cat }}</span>{{ spec.value }}</span>
                        </div>
                        <div class="rb-track">
                            <div class="rb-fill" :style="{ width: spec.percent + '%', background: spec.fill }"></div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import {mapActions, mapGetters} from 'vuex'
import { EventBus } from '../eventBus'
import {functions} from '../functions'
import gixtip from '../directives/tooltip'
import { BUILD_PARTS } from '../partOrder'
import { SHOW_PRICES } from '../config'
import { isPartUnavailable } from '../utils/partAvailability'
export default {
    name: 'RecentBuilds',
    directives: {
        gixtip,
    },
    data(){
        return {
            // Calibrated per-type reference scores (same scale as
            // functions.getBuildPerformance Details) used to turn a part's raw
            // score into a 0-100% bar. getMaxScores isn't reliable here (only
            // populated for browsed types, and graphics uses a different scale).
            perfMax: {
                cpu:        90,
                graphics:   700,
                ram:        48,
                storage:    80,
            }
        }
    },
    computed: {
        ...mapGetters([
            'getRecentBuilds',
            'getBuildsLoaded'
        ]),
        usesBuilderLayout(){
            return !!(this.$route && this.$route.path && this.$route.path.indexOf('/builder') === 0)
        },
        builds(){
            return (this.getRecentBuilds || []).map((build) => this.enrich(build))
        },
        showPrices(){
            return SHOW_PRICES
        }
    },
    methods:{
        ...mapActions([
            'fetchRecentBuilds',
            'loadMoreBuilds',
            'cloneBuild'
        ]),
        getNumericPrice: (value) => functions.parsePrice(value),
        enrich(build){
            var perf = functions.getBuildPerformance(build, false) || {}
            var details = perf.Details || {}
            var summary = this.calculateBuild(build)
            var overall = Math.min(100, Math.round(((perf.Total || 0) / 160) * 100))
            return {
                raw:          build,
                name:         build.name,
                thumb:        this.caseBg(build),
                hasPrice:     summary.pricedItemCount > 0 && summary.missingPriceCount === 0,
                priceWhole:   Math.trunc(summary.totalCost),
                priceCents:   functions.cents(summary.totalCost),
                tdp:          summary.tdp,
                chips:        this.makeChips(build),
                unavailableCount: this.unavailablePartCount(build),
                overall:      overall,
                overallColor: this.perfColor(overall),
                specs:        this.makeSpecs(build, details)
            }
        },
        caseBg(build){
            if(!build.case){ return null }
            if(build.case.mainImg){ return build.case.mainImg }
            try {
                var arr = JSON.parse(build.case.imgUrl)
                if(typeof arr[0] === 'string' && arr[0].includes('<a href')){ return arr[1] }
                return arr[0]
            } catch(e){ return null }
        },
        unavailablePartCount(build){
            var count = 0
            BUILD_PARTS.forEach((part) => {
                var value = build[part.backendKey]
                var items = Array.isArray(value) ? value : [value]
                items.forEach((item) => {
                    if(isPartUnavailable(item)){
                        count++
                    }
                })
            })
            return count
        },
        makeSpecs(build, details){
            var specs = []
            BUILD_PARTS.forEach((part) => {
                if(part.key == 'cpu' && build.processor && build.processor.model){
                    specs.push(this.spec('CPU', build.processor.model, details.cpu, 'cpu'))
                }
                if(part.key == 'graphics' && build.graphics && build.graphics.length){
                    var g = build.graphics
                    var gLabel = (g[0].model || 'Graphics') + (g.length > 1 ? ' x' + g.length : '')
                    specs.push(this.spec('GPU', gLabel, details.graphics, 'graphics'))
                }
                if(part.key == 'ram' && build.ram && build.ram.length){
                    var cap = functions.getItemValue({item: build.ram, field: 'capacity'})
                    specs.push(this.spec('RAM', cap + ' GB', details.ram, 'ram'))
                }
                if(part.key == 'storage' && build.storage && build.storage.length){
                    var scap = functions.getItemValue({item: build.storage, field: 'capacity'})
                    specs.push(this.spec('Storage', scap + ' TB', details.storage, 'storage'))
                }
            })
            return specs
        },
        spec(cat, value, score, type){
            var percent = this.toPercent(score, type)
            return { cat: cat, value: value, percent: percent, fill: this.perfFill(percent) }
        },
        // Key-spec chips (form factor + per-part capabilities) shown as a row.
        makeChips(build){
            var chips = []
            BUILD_PARTS.forEach((part) => {
                if(part.key == 'cpu' && build.processor && build.processor.specs){
                    var f = parseFloat(build.processor.specs.Core_Frequency)
                    if(f){ if(f > 1000){ f = f / 1000 } chips.push(f.toFixed(1) + ' GHz') }
                    var c = parseInt(build.processor.specs.Number_of_Cores)
                    if(c){ chips.push(c + (c > 1 ? ' cores' : ' core')) }
                }
                if(part.key == 'graphics' && build.graphics && build.graphics.length){
                    var v = this.gpuDetail(build.graphics)
                    if(v){ chips.push(v) }
                }
                if(part.key == 'motherboard'){
                    var ff = this.buildFormFactor(build)
                    if(ff){ chips.push(ff) }
                }
                if(part.key == 'ram' && build.ram && build.ram.length){
                    var rt = this.ramDetail(build.ram)
                    if(rt){ chips.push(rt) }
                }
                if(part.key == 'storage' && build.storage && build.storage.length){
                    var st = this.storageDetail(build.storage)
                    if(st){ chips.push(st) }
                }
            })
            return chips
        },
        gpuDetail(graphics){
            var vram = parseInt(graphics[0].specs && graphics[0].specs.graphics_ram_capacity)
            return vram ? vram + ' GB' : ''
        },
        ramDetail(ram){
            return (ram[0].compat && ram[0].compat.ram_type) || ''
        },
        storageDetail(storage){
            var s = storage[0]
            var iface = ((s.compat && s.compat.compat_interface) || '').toLowerCase()
            var size = ((s.compat && s.compat.size) || '').toLowerCase()
            var disk = ((s.specs && s.specs.Disk_Type) || '').toLowerCase()
            if(size.includes('m.2') || iface.includes('pci') || iface.includes('nvme')){ return 'NVMe' }
            if(disk.includes('ssd')){ return 'SSD' }
            if(disk.includes('hdd')){ return 'HDD' }
            return ''
        },
        buildFormFactor(build){
            if(build.motherboard && build.motherboard.compat && build.motherboard.compat.form_factor){ return build.motherboard.compat.form_factor }
            if(build.motherboard && build.motherboard.specs && build.motherboard.specs.Form_Factor){ return build.motherboard.specs.Form_Factor }
            if(build.case && build.case.compat && build.case.compat.form_factor){ return build.case.compat.form_factor }
            return ''
        },
        toPercent(score, type){
            if(!score || score <= 0){ return 0 }
            var max = this.perfMax[type] || 100
            return Math.max(2, Math.min(100, Math.round((score / max) * 100)))
        },
        perfColor(percent){
            if(percent < 34){ return '#c95e71' }
            if(percent < 67){ return '#c5b95a' }
            return '#16a989'
        },
        perfFill(percent){
            if(percent < 34){ return 'linear-gradient(90deg, rgba(201, 94, 113, 0.9), rgba(201, 94, 113, 0.72))' }
            if(percent < 67){ return 'linear-gradient(90deg, rgba(197, 185, 90, 0.92), rgba(197, 185, 90, 0.72))' }
            return 'linear-gradient(90deg, rgba(22, 169, 137, 0.92), rgba(22, 169, 137, 0.72))'
        },
        scoreTip(score){
            if(score >= 100){
                return 'High-end build - these parts top our 100-point performance scale, benchmarked against a high-end reference build.'
            }
            return 'Performance score: ' + score + ' of 100 - how this build compares to a high-end reference build.'
        },
        calculateBuild (build) {
            const localBuild = build
            var totalCost = 0
            var tdp = 0
            var pricedItemCount = 0
            var missingPriceCount = 0
            for (const item in localBuild){
                if (localBuild[item]){
                    if(typeof(localBuild[item]) == 'object'){
                        if(localBuild[item].compat && localBuild[item].amzn_price){
                            var singleItemPrice = this.getNumericPrice(localBuild[item].amzn_price)
                            if(singleItemPrice !== null){
                                totalCost += singleItemPrice
                                pricedItemCount++
                            }
                            else {
                                missingPriceCount++
                            }
                            tdp += (parseInt(localBuild[item].compat.tdp) || 0)
                        }
                        else if(localBuild[item].length >0){
                            for(var j = 0; j< localBuild[item].length; j++){
                                if(localBuild[item][j].type == 'ram'||localBuild[item][j].type == 'storage'||localBuild[item][j].type == 'gpu'|| localBuild[item][j].type == 'graphics'){
                                    var arrayItemPrice = this.getNumericPrice(localBuild[item][j].amzn_price)
                                    if(arrayItemPrice !== null){
                                        totalCost += arrayItemPrice
                                        pricedItemCount++
                                    }
                                    else {
                                        missingPriceCount++
                                    }
                                }
                                if(localBuild[item][j].type == 'ram'){
                                    tdp += (3*(parseInt(localBuild[item][j].compat.capacity) / 8) || 0)
                                }
                                else{
                                    tdp += (parseInt(localBuild[item][j].compat.tdp) || 0)
                                }
                            }
                        }
                    }
                    else if(typeof(localBuild[item]) !== 'number' && typeof(localBuild[item]) !== 'string') {
                        var nestedItemPrice = this.getNumericPrice(localBuild[item].amzn_price)
                        if(nestedItemPrice !== null){
                            totalCost += nestedItemPrice
                            pricedItemCount++
                        }
                        else {
                            missingPriceCount++
                        }
                        if(localBuild[item].type !== 'psu' && localBuild[item].type !== 'case'){
                            tdp += (parseInt(localBuild[item].compat.tdp) || 0)
                        }
                    }
                }
            }
            return {
                tdp: Math.round(tdp),
                totalCost: totalCost,
                pricedItemCount: pricedItemCount,
                missingPriceCount: missingPriceCount
            }
        },
        scrollToTop() {
            window.scrollTo(0,0);
            EventBus.$emit('typeChange', 'cpu')
        },
    },
    mounted(){
        this.fetchRecentBuilds({limit: 6})
    }
}
</script>
<style scoped>
.builds-container {
    margin-right: 10px;
    text-align: left;
}

@media screen and (min-width: 761px) and (max-width: 1200px) {
    .builds-container--builder {
        box-sizing: border-box;
        width: 100%;
        margin-right: 0;
        padding-right: 16px;
        padding-left: 96px;
    }
}

@media screen and (min-width: 761px) and (max-width: 940px) {
    .builds-container--builder {
        padding-right: 12px;
        padding-left: 90px;
    }
}

@media screen and (max-width: 760px) {
    .builds-container--builder {
        box-sizing: border-box;
        width: 100%;
        margin-right: 0;
        padding-right: 12px;
        padding-left: 12px;
    }
}

.builds-heading {
    margin: 0 0 0.4rem;
    padding: 0 0 14px 5px;
    border-bottom: 1px solid #434857;
    color: #cfd2da;
    font-size: 20px;
    font-weight: 600;
    letter-spacing: 0.8px;
    text-transform: uppercase;
}

.builds-list {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 0.9rem;
    padding: 14px 0;
}

.build-item {
    display: flex;
    flex-direction: column;
    min-width: 0;
    padding: 0.95rem 1rem 1.05rem;
    border: 1px solid rgba(67, 72, 87, 0.9);
    border-radius: 10px;
    background:
        radial-gradient(circle at 0% 0%, rgba(25, 151, 198, 0.08), rgba(25, 151, 198, 0) 38%),
        linear-gradient(180deg, rgba(27, 31, 42, 0.66), rgba(13, 16, 24, 0.72));
    transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
}

.build-item:hover {
    border-color: rgba(25, 151, 198, 0.6);
    box-shadow: 0 12px 30px rgba(0, 0, 0, 0.32), 0 0 0 1px rgba(25, 151, 198, 0.08);
    transform: translateY(-2px);
}

/* Header */
.rb-header {
    display: flex;
    gap: 0.85rem;
    align-items: stretch;
}

.rb-thumb {
    flex: 0 0 96px;
    width: 96px;
    height: 96px;
    border-radius: 8px;
    background-color: #ffffff;
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
    box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.5), 0 6px 16px rgba(0, 0, 0, 0.3);
}

.rb-thumb--empty {
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgba(8, 11, 17, 0.55);
    box-shadow: inset 0 0 0 1px rgba(67, 72, 87, 0.8);
    color: #4c5468;
    font-size: 32px;
}

.rb-headmain {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-width: 0;
}

.rb-title-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.rb-name {
    flex: 1 1 auto;
    min-width: 0;
    margin: 0;
    overflow: hidden;
    color: #e5e8ef !important;
    font-size: 19px;
    font-weight: 600;
    line-height: 1.15;
    white-space: nowrap;
    text-overflow: ellipsis;
}

.rb-score {
    flex: 0 0 auto;
    padding: 0.18rem 0.5rem;
    border: 1px solid;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 700;
    line-height: 1;
    white-space: nowrap;
}

.rb-score-max {
    font-size: 10px;
    font-weight: 600;
    opacity: 0.6;
}

.rb-score--highend {
    text-transform: uppercase;
    letter-spacing: 0.04em;
}

.rb-score-info {
    flex: 0 0 auto;
    color: #6b7280;
    font-size: 13px;
    cursor: pointer;
    transition: color 0.15s ease;
}

.rb-score-info:hover {
    color: #9198aa;
}

.rb-actions {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 0.6rem;
    margin-top: auto;
    padding-top: 0.55rem;
}

.rb-price {
    color: #e4d836;
    font-size: 22px;
    font-weight: 600;
    line-height: 1;
}

.rb-price-cur {
    margin-right: 1px;
    color: #9198aa;
    font-size: 14px;
    vertical-align: top;
}

.rb-price-cents {
    color: #b9b46a;
    font-size: 14px;
    vertical-align: top;
}

.rb-price-check {
    color: #9198aa;
    font-size: 14px;
    font-weight: 500;
}

.build-item .gc-button.rb-clone {
    width: 92px !important;
    height: 34px;
    margin: 0;
    padding: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 1px solid rgba(25, 151, 198, 0.72);
    border-radius: 6px;
    background: rgba(25, 151, 198, 0.06);
    color: #1997c6;
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0.4px;
    text-transform: uppercase;
    cursor: pointer;
    transition: border-color 0.18s ease, background 0.18s ease, box-shadow 0.18s ease, color 0.18s ease;
}

.build-item .gc-button.rb-clone:hover {
    border-color: #16b4ed;
    background: rgba(25, 151, 198, 0.16);
    box-shadow: 0 0 16px rgba(25, 151, 198, 0.14);
    color: #f4f8fb;
}

/* Key-spec chips (sit directly under the build title) */
.rb-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 0.35rem;
    margin-top: 0.5rem;
}

.rb-chip {
    display: inline-flex;
    align-items: center;
    gap: 0.28rem;
    padding: 0.24rem 0.5rem;
    border: 1px solid rgba(67, 72, 87, 0.92);
    border-radius: 6px;
    background: rgba(8, 11, 17, 0.42);
    color: #aeb4c3;
    font-size: 11.5px;
    font-weight: 600;
    line-height: 1;
    white-space: nowrap;
}

.rb-chip .icon {
    font-size: 11px;
    opacity: 0.85;
}

.rb-chip--watt {
    color: #9aa2b2;
}

.rb-chip.rb-chip--unavailable {
    border-color: rgba(224, 192, 126, 0.42);
    background: rgba(224, 192, 126, 0.1);
    color: #e0c07e;
}

/* Specs */
.rb-specs {
    display: flex;
    flex-direction: column;
    gap: 0.62rem;
    margin-top: 0.9rem;
    padding-top: 0.85rem;
    border-top: 1px solid rgba(67, 72, 87, 0.65);
}

.rb-spec {
    min-width: 0;
}

.rb-spec-head {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 0.6rem;
    margin-bottom: 0.32rem;
}

.rb-spec-name {
    display: block;
    min-width: 0;
    overflow: hidden;
    color: #cfd2da;
    font-size: 13.5px;
    font-weight: 500;
    white-space: nowrap;
    text-overflow: ellipsis;
}

.rb-spec-cat {
    display: inline-block;
    margin-right: 0.45rem;
    padding: 0.08rem 0.36rem;
    border-radius: 4px;
    background: rgba(25, 151, 198, 0.12);
    color: #7fc4e0;
    font-size: 9.5px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    vertical-align: middle;
}

.rb-track {
    width: 100%;
    height: 6px;
    border-radius: 999px;
    background: rgba(67, 72, 87, 0.5);
    overflow: hidden;
}

.rb-fill {
    height: 100%;
    border-radius: 999px;
    transition: width 0.5s ease, background 0.3s ease;
}

@media screen and (max-width: 1500px) {
    .builds-list {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
}

@media screen and (max-width: 900px) {
    .builds-list {
        grid-template-columns: minmax(0, 1fr);
    }
}

/* Refined build cards */
.builds-list {
    gap: 0.82rem;
}

.build-item {
    padding: 0.9rem 0.95rem 0.98rem;
    border-color: rgba(82, 91, 112, 0.64);
    border-radius: 8px;
    background:
        radial-gradient(circle at 0% 0%, rgba(25, 151, 198, 0.05), rgba(25, 151, 198, 0) 34%),
        linear-gradient(180deg, rgba(24, 28, 38, 0.58), rgba(10, 13, 21, 0.7));
    box-shadow:
        inset 0 1px 0 rgba(255, 255, 255, 0.025),
        0 10px 24px rgba(0, 0, 0, 0.18);
}

.build-item:hover {
    border-color: rgba(91, 104, 128, 0.82);
    box-shadow:
        inset 0 1px 0 rgba(255, 255, 255, 0.03),
        0 12px 28px rgba(0, 0, 0, 0.22);
    transform: translateY(-1px);
}

.rb-header {
    gap: 0.82rem;
}

.rb-thumb {
    flex-basis: 92px;
    width: 92px;
    height: 92px;
    box-sizing: border-box;
    padding: 7px;
    border: 1px solid rgba(197, 204, 216, 0.42);
    background-color: #e9edf2;
    background-origin: content-box;
    box-shadow:
        0 8px 18px rgba(0, 0, 0, 0.26),
        inset 0 0 16px rgba(14, 18, 26, 0.16),
        inset 0 0 0 1px rgba(255, 255, 255, 0.55);
}

.rb-name {
    color: #d8dde8 !important;
    font-size: 18px;
    font-weight: 600;
}

.rb-title-row {
    gap: 0.42rem;
}

.rb-score {
    padding: 0.18rem 0.48rem;
    background: rgba(8, 11, 17, 0.28);
    box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.02);
    font-size: 11px;
    opacity: 0.9;
}

.rb-score--highend {
    color: #73cbb9 !important;
    border-color: rgba(22, 169, 137, 0.58) !important;
    background: rgba(22, 169, 137, 0.08);
}

.rb-score-info {
    color: #626b7c;
}

.rb-chips {
    gap: 0.3rem;
    margin-top: 0.42rem;
}

.rb-chip {
    padding: 0.2rem 0.44rem;
    border-color: rgba(82, 91, 112, 0.56);
    background: rgba(8, 11, 17, 0.22);
    color: #959ead;
    font-size: 11px;
    font-weight: 600;
}

.rb-chip--watt {
    color: #87909f;
}

.rb-actions {
    align-items: center;
    margin-top: 0.54rem;
    padding-top: 0.48rem;
    border-top: 1px solid rgba(82, 91, 112, 0.32);
}

.rb-price {
    color: #c5b95a;
    font-size: 18px;
    font-weight: 600;
}

.rb-price-cur,
.rb-price-cents {
    color: #8992a2;
    font-size: 12px;
}

.rb-price-check {
    display: inline-flex;
    align-items: center;
    min-height: 26px;
    color: #a1a9b8;
    font-size: 13px;
}

.build-item .gc-button.rb-clone {
    width: 78px !important;
    height: 30px;
    border-color: rgba(25, 151, 198, 0.54);
    background: rgba(25, 151, 198, 0.035);
    color: #5fb8d5;
    font-size: 12px;
    letter-spacing: 0.02em;
}

.build-item .gc-button.rb-clone:hover {
    border-color: rgba(25, 151, 198, 0.74);
    background: rgba(25, 151, 198, 0.1);
    box-shadow: 0 0 12px rgba(25, 151, 198, 0.1);
    color: #d7eef7;
}

.rb-specs {
    gap: 0.52rem;
    margin-top: 0.72rem;
    padding-top: 0.72rem;
    border-top-color: rgba(82, 91, 112, 0.42);
}

.rb-spec-head {
    margin-bottom: 0.24rem;
}

.rb-spec-name {
    color: #bcc3cf;
    font-size: 12.5px;
    font-weight: 500;
}

.rb-spec-cat {
    margin-right: 0.4rem;
    padding: 0.07rem 0.34rem;
    background: rgba(25, 151, 198, 0.1);
    color: #68afca;
    font-size: 9px;
}

.rb-track {
    height: 4px;
    background: rgba(54, 62, 78, 0.54);
    box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.015);
}

.rb-fill {
    opacity: 0.88;
    box-shadow: none;
}

/* Compact meters: labels lead, bars support. */
.rb-spec {
    display: grid;
    grid-template-columns: minmax(0, 1fr) clamp(132px, 30%, 220px);
    align-items: center;
    gap: 0.72rem;
}

.rb-spec-head {
    min-width: 0;
    margin-bottom: 0;
}

.rb-track {
    width: 100%;
    justify-self: end;
}

@media screen and (max-width: 620px) {
    .rb-spec {
        grid-template-columns: 1fr;
        gap: 0.28rem;
    }

    .rb-track {
        justify-self: stretch;
    }
}
</style>
